// Workspace scanner for Quick Settings tunables.
//
// Pure function: reads the PASSED workspace, returns a row per tunable. No block mutation. Sources:
//   1. number_input / text_input blocks (ported from traderbots) — field 'VALUE'.
//   2. Numbers already wired into the settings blocks Store strategies use (money management
//      *_tradedefinitions, virtual_config*, tradeOptions stake/duration) — the math_number (usually a
//      shadow) on the input, field 'NUM'. If the input reads a variable, the math_number that variable
//      is set to inside "Run Once at Start" is used instead.
//   3. Numbers a strategy assigns to its own variables in "Run Once at Start" (DBot-style strategies keep
//      stake / take profit / stop loss there), labelled with the variable name. Counters — bumped by
//      math_change and starting at 0 — are skipped.
// Blocks a protected strategy hid (svgGroup_ removed in blockly/index.js loadWorkspace) are skipped,
// so Quick Settings only exposes values the user can already see and edit on the workspace.
/* global Blockly */
import { translate } from '../../../common/i18n';

const TUNABLE_TYPES = ['number_input', 'text_input'];
const TRADE_OPTION_INPUTS = ['AMOUNT', 'DURATION'];

const isVisible = block => {
    const svg = block.getSvgRoot && block.getSvgRoot();
    return !svg || svg.isConnected;
};

const isSettingsBlock = type =>
    /_tradedefinitions$/.test(type) || /^virtual_config/.test(type) || type === 'tradeOptions';

// Label text only: dropdowns on the same row (e.g. the duration unit) are not part of the name.
const rowText = input =>
    input.fieldRow
        .filter(f => !(f instanceof Blockly.FieldDropdown))
        .map(f => f.getText())
        .join(' ')
        .replace(/:\s*$/, '')
        .trim();

// The block whose "Run Once at Start" (INITIALIZATION) statement contains `block`, or null.
const initializationOwner = block => {
    for (let b = block; b.getParent(); b = b.getParent()) {
        if (b.getParent().getInputTargetBlock('INITIALIZATION') === b) return b.getParent();
    }
    return null;
};

const numberRow = (target, group, label, min) => ({
    id           : target.id,
    type         : 'math_number',
    group,
    label,
    value        : target.getFieldValue('NUM'),
    kind         : 'number',
    field        : 'NUM',
    min,
    orphan       : false,
    disambiguator: '',
});

// The math_number a variables_get resolves to: its single numeric assignment in "Run Once at Start".
const numberForVariable = (workspace, getter) => {
    const name = getter.getFieldValue('VAR');
    const setters = workspace
        .getAllBlocks()
        .filter(b => b.type === 'variables_set' && b.getFieldValue('VAR') === name && initializationOwner(b));
    if (setters.length !== 1) return null;
    const value = setters[0].getInputTargetBlock('VALUE');
    return value && value.type === 'math_number' ? value : null;
};

const settingsRows = (workspace, block) => {
    // Group header = the block's title label, as shown on the workspace.
    const group =
        block.type === 'tradeOptions'
            ? translate('Trade Options')
            : block.inputList[0].fieldRow.map(f => f.getText().trim()).find(Boolean) || '';
    return block.inputList
        .filter(input => input.type === Blockly.INPUT_VALUE)
        .filter(input => block.type !== 'tradeOptions' || TRADE_OPTION_INPUTS.includes(input.name))
        .map(input => {
            let target = input.connection && input.connection.targetBlock();
            if (target && target.type === 'variables_get') target = numberForVariable(workspace, target);
            if (!target || target.type !== 'math_number' || !isVisible(target)) return null;
            return numberRow(target, group, rowText(input) || input.name, 0);
        })
        .filter(Boolean);
};

const variableRows = blocks => {
    const counters = new Set(blocks.filter(b => b.type === 'math_change').map(b => b.getFieldValue('VAR')));
    return blocks
        .filter(b => b.type === 'variables_set')
        .map(b => {
            const owner = initializationOwner(b);
            const value = b.getInputTargetBlock('VALUE');
            if (!owner || !value || value.type !== 'math_number' || !isVisible(value)) return null;
            if (counters.has(b.getFieldValue('VAR')) && Number(value.getFieldValue('NUM')) === 0) return null;
            return numberRow(value, rowText(owner.getInput('INITIALIZATION')), b.getField('VAR').getText());
        })
        .filter(Boolean);
};

/**
 * Scan the workspace for tunables.
 * @param {Blockly.Workspace} workspace
 * @returns {Array<{id, type, group, label, value, kind:'number'|'text', field:'VALUE'|'NUM', min?,
 *            orphan:boolean, disambiguator:string}>}
 */
export const scanTunables = workspace => {
    if (!workspace || typeof workspace.getAllBlocks !== 'function') {
        return [];
    }
    const blocks = workspace.getAllBlocks().filter(b => b && isVisible(b));

    const inputRows = blocks.filter(b => TUNABLE_TYPES.includes(b.type)).map(block => ({
        id           : block.id,
        type         : block.type,
        group        : '',
        label        : block.getFieldValue('LABEL') || '',
        value        : block.getFieldValue('VALUE'),
        kind         : block.type === 'number_input' ? 'number' : 'text',
        field        : 'VALUE',
        orphan       : !block.outputConnection || block.outputConnection.targetBlock() === null,
        disambiguator: '',
    }));

    const seen = new Set();
    const rows = inputRows
        .concat(...blocks.filter(b => isSettingsBlock(b.type)).map(b => settingsRows(workspace, b)))
        .concat(variableRows(blocks))
        // a variable read by a settings input (or by two) resolves to the same math_number — list it once
        .filter(r => !seen.has(r.id) && seen.add(r.id));

    // Duplicate labels within a group are numbered 1, 2, … (no delete-on-collision).
    const key = r => `${r.group}|${r.label}`;
    const labelCounts = {};
    rows.forEach(r => {
        labelCounts[key(r)] = (labelCounts[key(r)] || 0) + 1;
    });
    const seenCounts = {};
    rows.forEach(r => {
        if (labelCounts[key(r)] > 1) {
            seenCounts[key(r)] = (seenCounts[key(r)] || 0) + 1;
            // eslint-disable-next-line no-param-reassign
            r.disambiguator = String(seenCounts[key(r)]);
        }
    });

    return rows;
};

export default scanTunables;
